import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function BrowseBySize() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const key = p.size || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    // Sort sizes descending by numeric first dimension (e.g. 800x800 before 300x300)
    const entries = Array.from(map.entries()).sort((a, b) => {
      const na = parseInt(a[0]) || 0;
      const nb = parseInt(b[0]) || 0;
      return nb - na;
    });
    return entries;
  }, [products]);

  if (loading) return <div className="p-20 text-center text-[#8A8A8A]" data-testid="bbs-loading">Loading catalog by size…</div>;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="browse-by-size-page">
      <div className="mb-12">
        <p className="label-eyebrow">Curated by Dimension</p>
        <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3">Browse by Size</h1>
        <p className="text-[#5C5C5C] mt-4 max-w-2xl">Pick the format that fits your space — from large-format 800×800 statement floors to intimate 300×300 wall tiles.</p>
        <div className="flex flex-wrap gap-2 mt-8">
          {groups.map(([size]) => (
            <a
              key={size}
              href={`#size-${size.replace(/[^0-9a-z]/gi, "")}`}
              className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-[#1A1A1A]/15 hover:border-[#1E2C5A] hover:text-[#1E2C5A] transition-colors"
              data-testid={`bbs-jump-${size}`}
            >{size}</a>
          ))}
        </div>
      </div>

      {groups.length === 0 && (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="bbs-empty">
          No products available.
        </div>
      )}

      {groups.map(([size, items]) => (
        <section key={size} id={`size-${size.replace(/[^0-9a-z]/gi, "")}`} className="mb-20" data-testid={`bbs-group-${size}`}>
          <div className="flex items-end justify-between mb-6 border-b border-[#1A1A1A]/10 pb-4">
            <div>
              <p className="label-eyebrow">Size</p>
              <h2 className="font-editorial text-4xl tracking-editorial mt-1">{size}</h2>
            </div>
            <p className="text-xs text-[#8A8A8A]">{items.length} tiles</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map(p => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="group block"
                data-testid={`bbs-product-${p.our_code}`}
              >
                <div className="aspect-square bg-[#F0EFEA] overflow-hidden mb-3">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <p className="label-eyebrow">{p.category} · {p.our_code}</p>
                <p className="font-medium mt-1 group-hover:text-[#1E2C5A] transition-colors">{p.name}</p>
                <p className="text-sm text-[#5C5C5C] mt-1">₹{p.offer_price} <span className="text-xs text-[#8A8A8A]">/ sq.ft</span></p>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-right">
            <Link to={`/catalog?size=${encodeURIComponent(size)}`} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1">
              See all in {size} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      ))}
    </div>
  );
}
